import { useState, useEffect } from "react";
import { T, Icon } from "../businessTheme";
import { supabase } from "../../lib/supabase";

function LocationCard({ loc, canEdit }) {
  const disabledStyle = !canEdit
    ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" }
    : {};

  return (
    <div style={{ padding: "20px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="MapPin" size={18} color={T.g600} strokeWidth={1.7} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: T.n400, background: T.n50, border: `1px solid ${T.n150}`, borderRadius: 99, padding: "3px 10px" }}>{loc.type}</span>
      </div>

      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.n900, marginBottom: 4 }}>{loc.label}</div>
        <div style={{ fontSize: 12.5, color: T.n400, lineHeight: 1.5 }}>{loc.address}</div>
      </div>

      <div style={{ display: "flex", gap: 18, paddingTop: 12, borderTop: `1px solid ${T.n100}` }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.n900 }}>{loc.units}</div>
          <div style={{ fontSize: 11, color: T.n400 }}>{loc.units === 1 ? "unit" : "units"}</div>
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: loc.activeJobs > 0 ? T.g600 : T.n900 }}>{loc.activeJobs}</div>
          <div style={{ fontSize: 11, color: T.n400 }}>active jobs</div>
        </div>
      </div>

      {/* Per-card actions */}
      <div style={{ display: "flex", gap: 8, paddingTop: 4 }}>
        <button
          style={{
            flex: 1, padding: "8px 0", borderRadius: 9, border: `1.5px solid ${T.n200}`,
            background: "transparent", color: T.n700, fontSize: 12.5, fontWeight: 600,
            fontFamily: T.sans, cursor: canEdit ? "pointer" : "not-allowed",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            ...disabledStyle,
          }}
        >
          <Icon name="Pencil" size={13} color={T.n500} strokeWidth={2} />
          Edit
        </button>
        <button
          style={{
            flex: 1, padding: "8px 0", borderRadius: 9, border: `1.5px solid ${T.r100 || "#fde8e8"}`,
            background: "transparent", color: T.r500 || "#e53e3e", fontSize: 12.5, fontWeight: 600,
            fontFamily: T.sans, cursor: canEdit ? "pointer" : "not-allowed",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            ...disabledStyle,
          }}
        >
          <Icon name="Trash2" size={13} color={T.r500 || "#e53e3e"} strokeWidth={2} />
          Delete
        </button>
      </div>
    </div>
  );
}

export default function LocationsSection({ account, plan, userRole }) {
  const [addOpen, setAddOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [newLoc, setNewLoc] = useState({ name: "", address: "", city: "", units: "" });
  const [saving, setSaving] = useState(false);

  const loadLocations = async () => {
    if (!account?.id) return;
    const [{ data: locRows }, { data: activeJobRows }] = await Promise.all([
      supabase
        .from("business_locations")
        .select("id, name, address, city, is_primary, units")
        .eq("business_id", account.id),
      supabase
        .from("job_requests")
        .select("location_id")
        .eq("business_id", account.id)
        .in("status", ["submitted", "matched", "confirmed"]),
    ]);

    const activeCounts = {};
    (activeJobRows || []).forEach(j => {
      if (j.location_id) activeCounts[j.location_id] = (activeCounts[j.location_id] || 0) + 1;
    });

    setLocations((locRows || []).map(loc => ({
      ...loc,
      activeJobs: activeCounts[loc.id] || 0,
    })));
  };
  useEffect(() => { loadLocations(); }, [account?.id]);

  const canEdit = userRole === "Owner" || userRole === "Admin";
  const atLimit = plan?.locations != null && locations.length >= plan.locations;
  const addDisabled = !canEdit || atLimit;

  const handleAddLocation = async () => {
    if (!newLoc.name.trim() || !newLoc.address.trim()) return;
    setSaving(true);
    const { error } = await supabase
      .from("business_locations")
      .insert({
        business_id: account.id,
        name: newLoc.name,
        address: newLoc.address,
        city: newLoc.city,
        units: parseInt(newLoc.units) || 0,
        is_primary: locations.length === 0,
      });
    setSaving(false);
    if (!error) {
      setNewLoc({ name: "", address: "", city: "", units: "" });
      setAddOpen(false);
      loadLocations();
    }
  };

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: canEdit ? 20 : 12 }}>
        <p style={{ fontSize: 13.5, color: T.n400, margin: 0 }}>
          {locations.length} location{locations.length !== 1 ? "s" : ""} on your account
          {plan?.locations != null && (
            <span style={{ color: T.n300 }}> &middot; {plan.locations} max on your plan</span>
          )}
        </p>
        <button
          onClick={() => !addDisabled && setAddOpen(true)}
          style={{
            padding: "10px 18px", borderRadius: 10, border: "none",
            background: addDisabled ? T.n200 : T.g500,
            color: addDisabled ? T.n400 : T.white,
            fontSize: 13, fontWeight: 600, fontFamily: T.sans,
            cursor: addDisabled ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 7,
            opacity: addDisabled ? 0.5 : 1,
            pointerEvents: addDisabled ? "none" : "auto",
            transition: "background 0.15s",
          }}
          onMouseEnter={e => { if (!addDisabled) e.currentTarget.style.background = T.g600; }}
          onMouseLeave={e => { if (!addDisabled) e.currentTarget.style.background = T.g500; }}
        >
          <Icon name="Plus" size={15} color={addDisabled ? T.n400 : T.white} strokeWidth={2.2} />
          Add location
        </button>
      </div>

      {/* Read-only notice */}
      {!canEdit && (
        <div style={{
          display: "flex", alignItems: "center", gap: 9, marginBottom: 20,
          padding: "10px 14px", borderRadius: 10,
          background: T.n50, border: `1px solid ${T.n150}`,
        }}>
          <Icon name="Lock" size={14} color={T.n400} strokeWidth={2} />
          <span style={{ fontSize: 12.5, color: T.n500, fontFamily: T.sans }}>
            View only — contact your account owner to make changes.
          </span>
        </div>
      )}

      {/* Location cards grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
        {locations.length === 0 ? (
          <p style={{ fontSize: 13.5, color: T.n400 }}>No locations added yet.</p>
        ) : (
          locations.map(loc => (
            <LocationCard key={loc.id} loc={{ ...loc, label: loc.name, type: loc.is_primary ? "Primary" : "Secondary" }} canEdit={canEdit} />
          ))
        )}
      </div>

      {/* Add location modal */}
      {addOpen && (
        <div
          onClick={() => setAddOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(15,17,23,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: "min(440px, 100%)", background: T.white, borderRadius: 18, padding: 26 }}
          >
            <h3 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, margin: "0 0 16px" }}>
              Add a location
            </h3>

            {[
              { key: "name", label: "Location name" },
              { key: "address", label: "Address" },
              { key: "city", label: "City" },
              { key: "units", label: "Units", type: "number" },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12.5, fontWeight: 600, color: T.n700, display: "block", marginBottom: 6 }}>
                  {f.label}
                </label>
                <input
                  type={f.type || "text"}
                  value={newLoc[f.key]}
                  onChange={e => setNewLoc(n => ({ ...n, [f.key]: e.target.value }))}
                  style={{
                    width: "100%", padding: "11px 14px", borderRadius: 10,
                    border: `1.5px solid ${T.n200}`, fontSize: 13.5,
                    fontFamily: T.sans, outline: "none", boxSizing: "border-box",
                  }}
                  placeholder={f.label}
                />
              </div>
            ))}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
              <button
                onClick={() => setAddOpen(false)}
                style={{
                  padding: "10px 18px", borderRadius: 10, border: `1.5px solid ${T.n200}`,
                  background: "transparent", color: T.n700, fontSize: 13, fontWeight: 600,
                  fontFamily: T.sans, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddLocation}
                disabled={saving}
                style={{
                  padding: "10px 22px", borderRadius: 10, border: "none",
                  background: T.g500, color: T.white, fontSize: 13, fontWeight: 600,
                  fontFamily: T.sans, cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? "Adding..." : "Add location"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}